import {
  Dropdown,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@carbon/react';
import { EmptyState, ListTable, StatTile, StatTiles, TrendChart, useFormat, type ListColumn } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { Change, ReportFrame } from './reportFrame';
import { rangeQuery, unwrap, useReportRange } from './reportRange';

type Summary = HoComponents['schemas']['ReportSalesSummaryDto'];
type Totals = HoComponents['schemas']['SalesTotalsDto'];
type StoreSales = HoComponents['schemas']['StoreSalesDto'];
type ProductMix = HoComponents['schemas']['ProductMixDto'];
type Product = HoComponents['schemas']['ProductSalesDto'];
type Hourly = HoComponents['schemas']['HourlySalesDto'];
type Channel = HoComponents['schemas']['ChannelSalesDto'];
type Tender = HoComponents['schemas']['TenderTotalsDto'];
type Tax = HoComponents['schemas']['TaxTotalsDto'];

const n = (value: number | string | null | undefined) => Number(value ?? 0);

/** Sales over a period across stores or for one, with a comparison, each day, each store and each channel (C1, C4, C5). */
export function SalesReportPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const [range, setRange] = useReportRange();
  const [compare, setCompare] = useState<'none' | 'previous' | 'last_year'>('previous');
  const summary = useQuery({
    queryKey: ['reports', 'summary', range, compare],
    queryFn: () =>
      unwrap<Summary>(
        hoApi.GET('/api/v1/reporting/sales/summary', { params: { query: { ...rangeQuery(range), compare } } }),
      ),
  });
  const channels = useQuery({
    queryKey: ['reports', 'channels', range],
    queryFn: () =>
      unwrap<Channel[]>(hoApi.GET('/api/v1/reporting/sales/channels', { params: { query: rangeQuery(range) } })),
  });
  const s = summary.data;
  const tile = (label: I18nKey, key: keyof Totals) => {
    const now = n(s?.current[key] as number | string | undefined);
    const before = s?.compare ? n(s.compare[key] as number | string) : undefined;
    return (
      <StatTile
        key={key}
        label={t(label)}
        value={format.number(now)}
        isLoading={summary.isLoading}
        caption={
          before !== undefined ? (
            <>
              <Change now={now} before={before} /> {t('reports.vs', { value: format.number(before) })}
            </>
          ) : undefined
        }
      />
    );
  };
  const compareItems = (['previous', 'last_year', 'none'] as const).map((id) => ({
    id,
    label: t(`reports.compare.${id}`),
  }));

  return (
    <ReportFrame
      title={t('reports.sales.title')}
      description={t('reports.sales.description')}
      range={range}
      onRange={setRange}
      error={summary.error ?? channels.error}
      csvName="sales"
      filters={
        <div className="pos-report-filters__field">
          <Dropdown
            id="report-compare"
            titleText={t('reports.compareWith')}
            label={t('reports.compareWith')}
            items={compareItems}
            itemToString={(o) => o?.label ?? ''}
            selectedItem={compareItems.find((o) => o.id === compare)}
            onChange={({ selectedItem }) => setCompare(selectedItem?.id ?? 'none')}
          />
        </div>
      }
      csv={
        s && s.days.length > 0
          ? () => [
              [
                t('reports.date'),
                t('reports.orders'),
                t('reports.guests'),
                t('reports.total'),
                t('reports.discounts'),
                t('reports.tax'),
                t('reports.refunds'),
                t('reports.voids'),
              ],
              ...s.days.map((d) => [
                d.businessDate,
                n(d.orderCount),
                n(d.guestCount),
                n(d.total),
                n(d.discountTotal),
                n(d.taxTotal),
                n(d.refundTotal),
                n(d.voidCount),
              ]),
              [],
              [
                t('reports.store'),
                t('reports.orders'),
                t('reports.total'),
                t('reports.averageCheck'),
                t('reports.sales.compareTotal'),
              ],
              ...s.stores.map((x) => [
                x.storeName || x.storeCode,
                n(x.orderCount),
                n(x.total),
                n(x.averageCheck),
                x.compareTotal == null ? '' : n(x.compareTotal),
              ]),
            ]
          : undefined
      }
    >
      <StatTiles>
        {tile('reports.total', 'total')}
        {tile('reports.orders', 'orderCount')}
        {tile('reports.averageCheck', 'averageCheck')}
        {tile('reports.guests', 'guestCount')}
      </StatTiles>
      {s && s.days.length > 0 && (
        <>
          <h2 className="pos-report__heading">{t('reports.sales.byDay')}</h2>
          <TrendChart
            type="bar"
            points={s.days.map((d) => ({ label: format.date(d.businessDate), value: n(d.total) }))}
            labelHeader={t('reports.date')}
            valueHeader={t('reports.total')}
            summary={t('reports.sales.byDaySummary', { total: format.number(n(s.current.total)) })}
            formatValue={(v) => format.number(v)}
          />
        </>
      )}
      <ListTable<StoreSales & { id: string }>
        title={t('reports.sales.byStore')}
        columns={[
          { key: 'storeName', header: t('reports.store'), render: (x) => x.storeName || x.storeCode },
          {
            key: 'orderCount',
            header: t('reports.orders'),
            numeric: true,
            render: (x) => format.number(n(x.orderCount)),
          },
          {
            key: 'guestCount',
            header: t('reports.guests'),
            numeric: true,
            render: (x) => format.number(n(x.guestCount)),
          },
          { key: 'total', header: t('reports.total'), numeric: true, render: (x) => format.number(n(x.total)) },
          {
            key: 'averageCheck',
            header: t('reports.averageCheck'),
            numeric: true,
            render: (x) => format.number(n(x.averageCheck)),
          },
          {
            key: 'discountTotal',
            header: t('reports.discounts'),
            numeric: true,
            render: (x) => format.number(n(x.discountTotal)),
          },
          { key: 'voidCount', header: t('reports.voids'), numeric: true, render: (x) => format.number(n(x.voidCount)) },
          {
            key: 'averageMinutesOpen',
            header: t('reports.sales.minutesOpen'),
            numeric: true,
            render: (x) => (x.averageMinutesOpen == null ? '' : format.number(Math.round(n(x.averageMinutesOpen)))),
          },
          ...(s?.compare
            ? [
                {
                  key: 'compareTotal',
                  header: t('reports.sales.growth'),
                  numeric: true,
                  render: (x: StoreSales) => <Change now={n(x.total)} before={n(x.compareTotal)} />,
                },
              ]
            : []),
        ]}
        rows={(s?.stores ?? []).map((x) => ({ ...x, id: x.storeId }))}
        ariaLabel={t('reports.sales.byStore')}
        loading={summary.isLoading}
        empty={{ heading: t('reports.noSales') }}
      />
      <ListTable<Channel & { id: string }>
        title={t('reports.sales.byChannel')}
        columns={[
          { key: 'channelName', header: t('reports.channel') },
          { key: 'saleTypeCode', header: t('reports.saleType') },
          {
            key: 'orderCount',
            header: t('reports.orders'),
            numeric: true,
            render: (x) => format.number(n(x.orderCount)),
          },
          { key: 'total', header: t('reports.total'), numeric: true, render: (x) => format.number(n(x.total)) },
        ]}
        rows={(channels.data ?? []).map((x) => ({ ...x, id: `${x.salesChannelId}-${x.saleTypeCode}` }))}
        ariaLabel={t('reports.sales.byChannel')}
        loading={channels.isLoading}
        empty={{ heading: t('reports.noSales') }}
      />
    </ReportFrame>
  );
}

/** Product mix (C3): what each product and category sold, its share, and its standard cost and margin where known (C6, C7). */
export function ProductMixPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const [range, setRange] = useReportRange();
  const mix = useQuery({
    queryKey: ['reports', 'products', range],
    queryFn: () =>
      unwrap<ProductMix>(
        hoApi.GET('/api/v1/reporting/sales/products', { params: { query: { ...rangeQuery(range), limit: 200 } } }),
      ),
  });
  const total = n(mix.data?.netTotal);
  const share = (amount: number) => (total === 0 ? '' : `${((amount / total) * 100).toFixed(1)}%`);
  const margin = (p: Product) =>
    p.costAmount == null || n(p.netAmount) === 0
      ? ''
      : `${(((n(p.netAmount) - n(p.costAmount)) / n(p.netAmount)) * 100).toFixed(1)}%`;
  const name = (p: Product) =>
    p.kind === 'open'
      ? t('reports.products.openItems')
      : p.kind === 'category' && !p.categoryId
        ? t('reports.products.noCategory')
        : p.name;
  const columns = (withKind: boolean): ListColumn<Product & { id: string }>[] => [
    { key: 'name', header: t('reports.products.product'), render: name },
    ...(withKind
      ? [
          {
            key: 'kind',
            header: t('reports.products.kind'),
            render: (p: Product) => (
              <Tag type={p.kind === 'deal' ? 'purple' : 'gray'}>{t(`reports.products.kinds.${p.kind}` as I18nKey)}</Tag>
            ),
          },
          { key: 'categoryName', header: t('reports.products.category'), render: (p: Product) => p.categoryName ?? '' },
        ]
      : []),
    { key: 'quantity', header: t('reports.quantity'), numeric: true, render: (p) => format.number(n(p.quantity)) },
    { key: 'netAmount', header: t('reports.net'), numeric: true, render: (p) => format.number(n(p.netAmount)) },
    { key: 'share', header: t('reports.share'), numeric: true, render: (p) => share(n(p.netAmount)) },
    { key: 'margin', header: t('reports.products.margin'), numeric: true, render: margin },
    {
      key: 'voidQuantity',
      header: t('reports.products.voided'),
      numeric: true,
      render: (p) => (n(p.voidQuantity) ? format.number(n(p.voidQuantity)) : ''),
    },
  ];
  const products = (mix.data?.products ?? []).map((p) => ({ ...p, id: `${p.kind}-${p.productId}` }));
  const categories = (mix.data?.categories ?? []).map((p) => ({ ...p, id: `c-${p.productId}` }));
  const best = products[0];
  const costed = products.filter((p) => p.costAmount != null);
  const costedNet = costed.reduce((sum, p) => sum + n(p.netAmount), 0);
  const costedCost = costed.reduce((sum, p) => sum + n(p.costAmount), 0);
  return (
    <ReportFrame
      title={t('reports.products.title')}
      description={t('reports.products.description')}
      range={range}
      onRange={setRange}
      error={mix.error}
      csvName="product-mix"
      csv={
        products.length > 0
          ? () => [
              [
                t('reports.products.product'),
                t('reports.products.kind'),
                t('reports.products.category'),
                t('reports.quantity'),
                t('reports.net'),
                t('reports.products.cost'),
              ],
              ...(mix.data?.products ?? []).map((p) => [
                name(p),
                p.kind,
                p.categoryName ?? '',
                n(p.quantity),
                n(p.netAmount),
                p.costAmount == null ? null : n(p.costAmount),
              ]),
            ]
          : undefined
      }
    >
      <StatTiles>
        <StatTile label={t('reports.net')} value={format.number(total)} isLoading={mix.isLoading} />
        <StatTile
          label={t('reports.products.bestSeller')}
          value={best ? name(best) : '—'}
          caption={best ? `${format.number(n(best.netAmount))} · ${share(n(best.netAmount))}` : undefined}
          isLoading={mix.isLoading}
        />
        <StatTile
          label={t('reports.products.costedMargin')}
          value={costedNet === 0 ? '—' : `${(((costedNet - costedCost) / costedNet) * 100).toFixed(1)}%`}
          caption={t('reports.products.costedCaption', { share: share(costedNet) || '—' })}
          isLoading={mix.isLoading}
        />
      </StatTiles>
      <ListTable
        title={t('reports.products.byCategory')}
        columns={columns(false)}
        rows={categories}
        ariaLabel={t('reports.products.byCategory')}
        loading={mix.isLoading}
        empty={{ heading: t('reports.noSales') }}
      />
      <ListTable
        title={t('reports.products.top')}
        columns={columns(true)}
        rows={products}
        ariaLabel={t('reports.products.top')}
        loading={mix.isLoading}
        empty={{ heading: t('reports.noSales') }}
      />
    </ReportFrame>
  );
}

/** Sales by weekday and hour (C2): one row per hour, one column per weekday, and the hours' totals as a chart. */
export function HourlyReportPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const [range, setRange] = useReportRange();
  const hourly = useQuery({
    queryKey: ['reports', 'hourly', range],
    queryFn: () =>
      unwrap<Hourly[]>(hoApi.GET('/api/v1/reporting/sales/hourly', { params: { query: rangeQuery(range) } })),
  });
  const rows = hourly.data ?? [];
  const hours = [...new Set(rows.map((r) => n(r.hour)))].sort((a, b) => a - b);
  const cell = (weekday: number, hour: number) => rows.find((r) => n(r.weekday) === weekday && n(r.hour) === hour);
  const byHour = hours.map((hour) => ({
    label: `${String(hour).padStart(2, '0')}:00`,
    value: rows.filter((r) => n(r.hour) === hour).reduce((s, r) => s + n(r.total), 0),
  }));
  const weekdays = [1, 2, 3, 4, 5, 6, 7];
  const peakHour = byHour.reduce<(typeof byHour)[number] | undefined>(
    (a, b) => (!a || b.value > a.value ? b : a),
    undefined,
  );
  const peakDay = weekdays
    .map((day) => ({ day, value: rows.filter((r) => n(r.weekday) === day).reduce((s, r) => s + n(r.total), 0) }))
    .filter((d) => d.value > 0)
    .reduce<{ day: number; value: number } | undefined>((a, b) => (!a || b.value > a.value ? b : a), undefined);
  const dayName = (d: number) => t(`reports.weekdays.${d}` as I18nKey);
  return (
    <ReportFrame
      title={t('reports.hours.title')}
      description={t('reports.hours.description')}
      range={range}
      onRange={setRange}
      error={hourly.error}
      csvName="sales-by-hour"
      csv={
        hours.length > 0
          ? () => [
              [t('reports.hours.hour'), ...weekdays.map(dayName)],
              ...hours.map((h) => [`${String(h).padStart(2, '0')}:00`, ...weekdays.map((d) => n(cell(d, h)?.total))]),
            ]
          : undefined
      }
    >
      <StatTiles>
        <StatTile
          label={t('reports.hours.busiestHour')}
          value={peakHour ? peakHour.label : '—'}
          caption={peakHour ? format.number(peakHour.value) : undefined}
          isLoading={hourly.isLoading}
        />
        <StatTile
          label={t('reports.hours.busiestDay')}
          value={peakDay ? dayName(peakDay.day) : '—'}
          caption={peakDay ? format.number(peakDay.value) : undefined}
          isLoading={hourly.isLoading}
        />
      </StatTiles>
      {byHour.length > 0 && (
        <>
          <h2 className="pos-report__heading">{t('reports.hours.summary')}</h2>
          <TrendChart
            type="bar"
            points={byHour}
            labelHeader={t('reports.hours.hour')}
            valueHeader={t('reports.total')}
            summary={t('reports.hours.summary')}
            formatValue={(v) => format.number(v)}
          />
        </>
      )}
      <TableContainer title={t('reports.hours.grid')} description={t('reports.hours.note')}>
        <div className="pos-report__scroll">
          <Table aria-label={t('reports.hours.grid')} useZebraStyles>
            <TableHead>
              <TableRow>
                <TableHeader>{t('reports.hours.hour')}</TableHeader>
                {weekdays.map((d) => (
                  <TableHeader key={d} className="pos-report__number">
                    {dayName(d)}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {hours.map((h) => (
                <TableRow key={h}>
                  <TableCell>{`${String(h).padStart(2, '0')}:00`}</TableCell>
                  {weekdays.map((d) => {
                    const c = cell(d, h);
                    return (
                      <TableCell key={d} className="pos-report__number">
                        {c ? `${format.number(n(c.total))} · ${format.number(n(c.orderCount))}` : ''}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {!hourly.isLoading && hours.length === 0 && <EmptyState heading={t('reports.noSales')} />}
      </TableContainer>
    </ReportFrame>
  );
}

/** What was taken per payment method (D2) and the output tax per store and rate (D1), for the accountant. */
export function PaymentsTaxReportPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const [range, setRange] = useReportRange();
  const tenders = useQuery({
    queryKey: ['reports', 'tenders', range],
    queryFn: () =>
      unwrap<Tender[]>(hoApi.GET('/api/v1/reporting/payments/tenders', { params: { query: rangeQuery(range) } })),
  });
  const taxes = useQuery({
    queryKey: ['reports', 'tax', range],
    queryFn: () => unwrap<Tax[]>(hoApi.GET('/api/v1/reporting/tax/summary', { params: { query: rangeQuery(range) } })),
  });
  const tenderRows = (tenders.data ?? []).map((x) => ({ ...x, id: x.paymentMethodId }));
  const taxRows = (taxes.data ?? []).map((x, i) => ({ ...x, id: `${x.storeId}-${x.taxName}-${i}` }));
  const net = tenderRows.reduce((s, x) => s + n(x.net), 0);
  const tax = taxRows.reduce((s, x) => s + n(x.taxAmount), 0);
  return (
    <ReportFrame
      title={t('reports.payments.title')}
      description={t('reports.payments.description')}
      range={range}
      onRange={setRange}
      error={tenders.error ?? taxes.error}
      csvName="payments-tax"
      csv={
        tenderRows.length + taxRows.length > 0
          ? () => [
              [
                t('reports.payments.method'),
                t('reports.payments.paid'),
                t('reports.payments.refunded'),
                t('reports.net'),
                t('reports.payments.count'),
              ],
              ...tenderRows.map((x) => [x.name, n(x.paid), n(x.refunded), n(x.net), n(x.count)]),
              [],
              [
                t('reports.store'),
                t('reports.tax'),
                t('reports.payments.rate'),
                t('reports.payments.taxable'),
                t('reports.tax'),
              ],
              ...taxRows.map((x) => [x.storeName, x.taxName, n(x.rate), n(x.taxableAmount), n(x.taxAmount)]),
            ]
          : undefined
      }
    >
      <StatTiles>
        <StatTile label={t('reports.payments.netTaken')} value={format.number(net)} isLoading={tenders.isLoading} />
        <StatTile label={t('reports.payments.outputTax')} value={format.number(tax)} isLoading={taxes.isLoading} />
      </StatTiles>
      <ListTable
        title={t('reports.payments.byMethod')}
        columns={[
          { key: 'name', header: t('reports.payments.method') },
          {
            key: 'count',
            header: t('reports.payments.count'),
            numeric: true,
            render: (x) => format.number(n(x.count)),
          },
          { key: 'paid', header: t('reports.payments.paid'), numeric: true, render: (x) => format.number(n(x.paid)) },
          {
            key: 'refunded',
            header: t('reports.payments.refunded'),
            numeric: true,
            render: (x) => format.number(n(x.refunded)),
          },
          { key: 'net', header: t('reports.net'), numeric: true, render: (x) => format.number(n(x.net)) },
        ]}
        rows={tenderRows}
        ariaLabel={t('reports.payments.byMethod')}
        loading={tenders.isLoading}
        empty={{ heading: t('reports.payments.none') }}
      />
      <ListTable
        title={t('reports.payments.taxByStore')}
        columns={[
          { key: 'storeName', header: t('reports.store') },
          { key: 'taxName', header: t('reports.tax') },
          {
            key: 'taxableAmount',
            header: t('reports.payments.taxable'),
            numeric: true,
            render: (x) => format.number(n(x.taxableAmount)),
          },
          { key: 'taxAmount', header: t('reports.tax'), numeric: true, render: (x) => format.number(n(x.taxAmount)) },
        ]}
        rows={taxRows}
        ariaLabel={t('reports.payments.taxByStore')}
        loading={taxes.isLoading}
        empty={{ heading: t('reports.noSales') }}
      />
    </ReportFrame>
  );
}
